import { API } from 'aws-amplify';

const round2 = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? Math.round(x * 100) / 100 : 0;
};

// O Timestream devolve UTC no formato "YYYY-MM-DD HH:MM:SS.fffffffff": sem 'T' e
// sem 'Z'. O V8 interpreta essa forma como hora LOCAL, adiantando cada instante
// pelo offset do navegador (+3h no Brasil). Normaliza para epoch UTC em ms.
export const parseTimestreamTime = (value) => {
  if (value === null || value === undefined) return NaN;
  // Já é epoch (segundos ou milissegundos)
  if (typeof value === 'number') return value < 1e11 ? value * 1000 : value;
  const s = String(value).trim();
  // Já traz fuso explícito ('Z' ou ±HH:MM): o parser nativo acerta
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) return new Date(s).getTime();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?$/);
  if (!m) return new Date(s).getTime();
  const frac = (m[3] || '').slice(0, 3).padEnd(3, '0');
  return Date.parse(`${m[1]}T${m[2]}.${frac}Z`);
};

export const queryTimestreamData = async (vehiclePlate, startTime, endTime) => {
  try {
    const response = await API.get(
      'SimemapTenants',
      `/timestream/vehicle/${vehiclePlate}?start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`
    );

    const rows = response?.data || [];

    const processedData = rows
      .filter((item) => item?.time && item?.value !== null && item?.value !== undefined)
      .flatMap((item) => {
        const ts = parseTimestreamTime(item.time);

        return [
          { timestamp: ts, measureName: 'temperature', value: round2(item.value) },
          { timestamp: ts, measureName: 'voltage', value: Number(item.voltage/1000 ?? 0) },
          { timestamp: ts, measureName: 'velocity', value: Number(item.velocity ?? 0) }
        ];
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    return { measures: processedData, rows };
  } catch (error) {
    console.error('Error querying Timestream:', error);
    return { measures: [], rows: [] };
  }
};

export const queryTimestreamDevicesAll = async (startTime, endTime) => {
  try {
    const response = await API.get(
      'SimemapTenants',
      `/timestream/vehicle/all?start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`
    );
    const raw = typeof response === 'object' && response !== null ? (response.data ?? response) : response;
    const candidateArrays = [
      Array.isArray(raw) ? raw : null,
      Array.isArray(raw?.data) ? raw.data : null,
      Array.isArray(raw?.res?.data) ? raw.res.data : null,
      Array.isArray(raw?.res) ? raw.res : null,
      Array.isArray(raw?.rows) ? raw.rows : null,
      Array.isArray(raw?.devices) ? raw.devices : null,
      Array.isArray(raw?.data?.devices) ? raw.data.devices : null
    ].filter(Boolean);
    const rows = candidateArrays.length ? candidateArrays[0] : [];
    let ids = [];
    if (Array.isArray(rows)) {
      if (rows.length > 0 && typeof rows[0] === 'string') {
        ids = rows.filter(Boolean).map(String);
      } else {
        // device_id, nunca a placa: esse id vira o segmento do tópico MQTT
        // (Simemap/+/+/+/<device_id>/mtudata/log) e a chave da consulta ao
        // Timestream. A placa só serve para exibição.
        ids = rows.map((item) => (
          item?.device_id
        )).filter(Boolean);
      }
    }
    ids = Array.from(new Set(ids));
    try {
      // console.log('queryTimestreamDevicesAll response:', raw);
      // console.log('queryTimestreamDevicesAll extracted rows count:', rows.length);
      // console.log('queryTimestreamDevicesAll ids:', ids);
    } catch(e){}
    return ids;
  } catch (error) {
    console.error('Error querying Timestream devices (all):', error);
    return [];
  }
};

export const queryLastKnownPosition = async (vehiclePlate) => {
  try {
    const endTime = new Date().toISOString();
    const startTime = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(); // 7 days back
    
    const response = await API.get(
      'SimemapTenants',
      `/timestream/vehicle/${vehiclePlate}?start=${encodeURIComponent(startTime)}&end=${encodeURIComponent(endTime)}`
    );

    const rows = response?.data || [];
    if (rows.length === 0) return null;

    const byNewest = (a, b) => (
      parseTimestreamTime(b.time ?? b.timestamp) - parseTimestreamTime(a.time ?? a.timestamp)
    );

    // Most recent reading overall: temperature/battery are reported even with no GPS fix
    const latest = [...rows].sort(byNewest)[0];

    // Last known position may be older than `latest` — or absent, when the device
    // has never had a fix in this window (lat/long come as 0). The row still shows,
    // flagged as "GPS ausente" by TrucksTable.
    const lastFix = [...rows]
      .filter(item =>
        Number.isFinite(Number(item.latitude ?? item.lat)) &&
        Number.isFinite(Number(item.longitude ?? item.long)) &&
        Number(item.latitude ?? item.lat) !== 0 &&
        Number(item.longitude ?? item.long) !== 0
      )
      .sort(byNewest)[0];

    return {
      device_id: vehiclePlate,
      vehicle_plate: latest.vehicle_plate || vehiclePlate,
      NameDevice: latest.NameDevice || vehiclePlate,
      lat: lastFix ? Number(lastFix.latitude ?? lastFix.lat) : 0,
      long: lastFix ? Number(lastFix.longitude ?? lastFix.long) : 0,
      timestamp: Math.floor(parseTimestreamTime(latest.time ?? latest.timestamp) / 1000),
      velocity: Number(latest.velocity ?? 0),
      temps: latest.value !== undefined ? [{
        name: 'Sensor',
        value: Number(latest.value),
        voltage: Number(latest.voltage ?? 0)
      }] : []
    };
  } catch (error) {
    console.error(`Error querying last position for ${vehiclePlate}:`, error);
    return null;
  }
};
